import React, { useEffect, useState } from "react";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../config/firebase";
import sk from "./sk.png"
import pfp from "./Default_pfp.svg.png"

const Card = ({ student, pin, onStatusChange }) => {
  const [guardians, setGuardians] = useState([]);
  const [siblings, setSiblings] = useState([]);
  const [selectedGuardian, setSelectedGuardian] = useState("");
  const [disabledButtons, setDisabledButtons] = useState({}); // Tracks disabled state for buttons

  useEffect(() => {
    const fetchGroupData = async () => {
      if (!student?.group_id) return;

      try {
        const groupDocRef = doc(db, "groups", student.group_id);
        const groupDocSnap = await getDoc(groupDocRef);

        if (groupDocSnap.exists()) {
          const groupData = groupDocSnap.data();

          // Fetch guardian details
          const guardianPromises = groupData.guardian_ids.map(async (guardianId) => {
            const guardianDocRef = doc(db, "guardians", guardianId);
            const guardianDocSnap = await getDoc(guardianDocRef);
            return guardianDocSnap.exists() ? { id: guardianId, ...guardianDocSnap.data() } : null;
          });
          const fetchedGuardians = await Promise.all(guardianPromises);
          setGuardians(fetchedGuardians.filter((guardian) => guardian !== null));

          // Fetch sibling details
          const siblingPromises = groupData.student_ids
            .filter((id) => id !== student.id) // Exclude current student
            .map(async (siblingId) => {
              const siblingDocRef = doc(db, "students", siblingId);
              const siblingDocSnap = await getDoc(siblingDocRef);
              return siblingDocSnap.exists() ? { id: siblingId, ...siblingDocSnap.data() } : null;
            });
          const fetchedSiblings = await Promise.all(siblingPromises);
          setSiblings(fetchedSiblings.filter((sibling) => sibling !== null));
        } else {
          console.error("Group document not found for group_id:", student.group_id);
        }
      } catch (error) {
        console.error("Error fetching group data:", error);
      }
    };

    fetchGroupData();
    resetGuardianSelection(); // Reset guardian selection on student change
  }, [student]);

  const resetGuardianSelection = () => setSelectedGuardian("");

  const handleSignOut = async (id, name, grade) => {
    const timestamp = new Date().toISOString();

    try {
      // Update `students` collection
      const studentDocRef = doc(db, "students", id);
      await updateDoc(studentDocRef, { present: false });

      // Add to `pickup_logs`
      const logDocRef = doc(db, "pickup_logs", id);
      const logDocSnap = await getDoc(logDocRef);

      const newPickup = {
        guardian_id: selectedGuardian,
        license_plate_id: null,
        timestamp,
      };

      if (logDocSnap.exists()) {
        const existingData = logDocSnap.data();
        await updateDoc(logDocRef, {
          pickups: [...existingData.pickups, newPickup],
        });
      } else {
        await setDoc(logDocRef, { pickups: [newPickup] });
      }

      // Disable the button for the signed-out student
      setDisabledButtons((prev) => ({ ...prev, [id]: true }));
      console.log(`Student ${name} signed out successfully by Guardian ${selectedGuardian}.`);
      onStatusChange(); // Trigger refresh
    } catch (error) {
      console.error("Error signing out student:", error);
    }
  };

  return (
    <div className="card-container w-full bg-white rounded-md shadow-md p-6">
      {/* Top Section: Student Details and Image */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">
            {student.name || "Student Name"}
          </h2>
          <div className="space-y-1">
            <p>
              <span className="font-semibold">ID:</span> {student.id || "N/A"}
            </p>
            <p>
              <span className="font-semibold">Grade:</span> {student.grade || "N/A"}
            </p>
            <p>
              <span className="font-semibold">Status:</span>{" "}
              {student.present ? (
                <span className="text-green-500">Present</span>
              ) : (
                <span className="text-red-500">Picked Up</span>
              )}
            </p>
          </div>
        </div>
        <img
          src={student.image || pfp} // Add student image
          alt={`${student.name || "Student"}'s photo`}
          className="w-20 h-20 rounded-full object-cover"
        />
      </div>

      {/* Guardian Selection */}
      <div className="mb-4">
        <h3 className="font-semibold text-lg text-gray-700">Select Guardian</h3>
        {guardians.length > 0 ? (
          <select
            value={selectedGuardian}
            onChange={(e) => setSelectedGuardian(e.target.value)}
            className="w-full p-2 border rounded"
          >
            <option value="">Choose a Guardian</option>
            {guardians.map((guardian) => (
              <option key={guardian.id} value={guardian.id}>
                {guardian.name} - {guardian.relationship}
              </option>
            ))}
          </select>
        ) : (
          <p>No guardians available for selection.</p>
        )}
      </div>

      {/* Other Students Section */}
      <div className="mb-4">
        <h3 className="font-semibold text-lg text-gray-700">Other Students</h3>
        {siblings.length > 0 ? (
          <div className="space-y-2">
            {siblings.map((sibling) => (
              <div
                key={sibling.id}
                className="flex items-center justify-between border-b py-2"
              >
                <div className="flex-1">
                  <span className="font-semibold">Name:</span> {sibling.name}
                </div>
                <div className="flex-1 text-center">
                  <span className="font-semibold">Grade:</span> {sibling.grade}
                </div>
                <button
                  onClick={() =>
                    handleSignOut(sibling.id, sibling.name, sibling.grade)
                  }
                  disabled={
                    !selectedGuardian || disabledButtons[sibling.id] || !sibling.present
                  }
                  className={`ml-2 px-3 py-1 text-xs rounded ${
                    selectedGuardian && sibling.present && !disabledButtons[sibling.id]
                      ? "bg-red-500 text-white hover:bg-red-600"
                      : "bg-gray-400 text-gray-700 cursor-not-allowed"
                  }`}
                >
                  Sign Out
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p>No sibling info available.</p>
        )}
      </div>

      {/* Action Button */}
      <div className="mt-6">
        <button
          onClick={() => handleSignOut(student.id, student.name, student.grade)}
          disabled={
            !selectedGuardian || disabledButtons[student.id] || !student.present
          }
          className={`w-full px-4 py-2 rounded-md ${
            selectedGuardian && student.present && !disabledButtons[student.id]
              ? "bg-blue-500 text-white hover:bg-blue-600"
              : "bg-gray-400 text-gray-700 cursor-not-allowed"
          }`}
        >
          Sign Out
        </button>
      </div>
    </div>
  );
};

export default Card;
